import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalizeLocale, type SupportedLocale } from '@evi/shared';
import i18n, { LOCALE_STORAGE_KEY } from '@/lib/i18n';
import { updatePreferences } from '@/lib/api';

/**
 * UI-language store, mirroring the evieVoice pattern:
 *   - setLocale: user picked a language (Settings) — switch i18next, cache the
 *     choice per user, and fire-and-forget the server write so it follows the
 *     driver across devices.
 *   - hydrateLocale: seed from the server preference on sign-in (useLocaleSync)
 *     — switch i18next + cache, WITHOUT writing back to the server.
 *
 * i18next holds the live active language; this store owns the durable
 * per-user choice + the server round-trip.
 */
function persistActive(locale: SupportedLocale): void {
  void i18n.changeLanguage(locale);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* private mode / no storage — i18next still holds it in memory */
  }
}

interface LocaleState {
  localeByUser: Record<string, SupportedLocale>;
  setLocale: (userId: string | null, locale: SupportedLocale) => void;
  hydrateLocale: (userId: string | null, locale: SupportedLocale) => void;
}

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set, get) => ({
      localeByUser: {},
      setLocale: (userId, locale) => {
        const norm = normalizeLocale(locale);
        persistActive(norm);
        if (userId) set({ localeByUser: { ...get().localeByUser, [userId]: norm } });
        // User-initiated → persist to the server preference.
        void updatePreferences({ locale: norm }).catch(() => {});
      },
      hydrateLocale: (userId, locale) => {
        const norm = normalizeLocale(locale);
        persistActive(norm);
        if (userId) set({ localeByUser: { ...get().localeByUser, [userId]: norm } });
        // Server is the source of truth here — no write-back.
      },
    }),
    { name: 'evi-web-locale', partialize: (s) => ({ localeByUser: s.localeByUser }) },
  ),
);
